import React, { useEffect, useRef } from 'react';
import HomeHeader from '@/components/HomeHeader';
import Footer from '@/components/homepage/Footer';
import { usePageMeta } from '@/hooks/usePageMeta';
import TableOfContents from '@/components/resources/TableOfContents';
import EmailCapture from '@/components/resources/EmailCapture';

const TITLE = 'Pure Charity vs Devotion Fund: Sending Organization or Individual Support Platform?';
const DESCRIPTION =
    "An honest comparison from a competing founder: Pure Charity's org-level, custom-priced fiscal-sponsorship infrastructure vs Devotion Fund's self-serve, 5-7% all-in platform built for the individual missionary raising personal support.";

const JSON_LD = {
    '@context': 'https://schema.org',
    '@graph': [
        {
            '@type': 'Article',
            headline: TITLE,
            description: DESCRIPTION,
            image: 'https://devotionfund.com/resources/cover20.jpg',
            datePublished: '2026-07-24',
            dateModified: '2026-07-24',
            author: { '@type': 'Organization', name: 'Devotion Fund', url: 'https://devotionfund.com' },
            publisher: {
                '@type': 'Organization',
                name: 'Devotion Fund',
                logo: { '@type': 'ImageObject', url: 'https://devotionfund.com/logo.png' },
            },
            mainEntityOfPage: {
                '@id': 'https://devotionfund.com/resources/pure-charity-vs-devotion-fund',
                '@type': 'WebPage',
            },
        },
        {
            '@type': 'FAQPage',
            mainEntity: [
                {
                    '@type': 'Question',
                    name: 'Is Pure Charity a good fit for an individual missionary raising personal support?',
                    acceptedAnswer: {
                        '@type': 'Answer',
                        text: "Usually not on its own. Pure Charity is built for sending organizations, agencies, and team-based campaigns operating under an umbrella 501(c)(3) — not for a solo missionary self-onboarding in minutes. If you're an individual trying to set up your own page, it isn't really built for you.",
                    },
                },
                {
                    '@type': 'Question',
                    name: 'How much does Pure Charity cost?',
                    acceptedAnswer: {
                        '@type': 'Answer',
                        text: "Pure Charity doesn't publish a fee. Pricing is custom and negotiated per organization, which is why our full seven-platform cost comparison deliberately leaves it out of the math — there's no single honest number to show an individual missionary.",
                    },
                },
                {
                    '@type': 'Question',
                    name: 'Is Pure Charity or Devotion Fund cheaper?',
                    acceptedAnswer: {
                        '@type': 'Answer',
                        text: "There's no fair apples-to-apples number, because Pure Charity's pricing is negotiated per organization and unpublished, while Devotion Fund is a public, all-in 5% (bank transfer) or 7% (card). If you need a published rate you can plan a budget around, that alone may decide it.",
                    },
                },
                {
                    '@type': 'Question',
                    name: 'Who should choose Pure Charity over Devotion Fund?',
                    acceptedAnswer: {
                        '@type': 'Answer',
                        text: "Missions pastors and agency leaders running a centralized, multi-team campaign who need 501(c)(3) compliance and fund-governance infrastructure across many missionaries at once. That's a genuinely different job than an individual raising their own recurring support.",
                    },
                },
            ],
        },
    ],
};

export default function PureCharityVsDevotionFund() {
    usePageMeta(TITLE, DESCRIPTION, { path: '/resources/pure-charity-vs-devotion-fund' });

    const articleRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const el = document.createElement('script');
        el.type = 'application/ld+json';
        el.setAttribute('data-pc-vs-df-jsonld', 'true');
        el.textContent = JSON.stringify(JSON_LD);
        document.head.appendChild(el);
        return () => {
            el.remove();
        };
    }, []);

    return (
        <div className="min-h-screen bg-white flex flex-col">
            <HomeHeader />
            <div className="w-full h-64 md:h-80 bg-gray-100 overflow-hidden">
                <img src="/resources/cover20.jpg" alt="A missions team praying together before a trip" className="w-full h-full object-cover" />
            </div>
            <div className="max-w-3xl mx-auto px-4 py-12 w-full">
                <h1 className="text-3xl font-bold mb-6">{TITLE}</h1>
                <TableOfContents targetRef={articleRef} />
                <div className="prose max-w-none" ref={articleRef}>
                    <p>Pure Charity comes up less often than GoFundMe or Patreon in these questions, but when it does, the honest answer isn't really "which platform is cheaper" — it's "which of these is even trying to solve your problem." I run a competing platform, so weigh what follows accordingly. But Pure Charity and Devotion Fund aren't built for the same person.</p>

                    <h2>The short version</h2>

                    <p>Pure Charity is <strong>organization-level fundraising infrastructure</strong> — built for sending agencies, mission trips, and team-based campaigns that operate under an umbrella 501(c)(3), with custom pricing negotiated per organization. Devotion Fund is a <strong>self-serve platform for the individual missionary</strong> — you set up your own recurring support page in about ten minutes, at a published, all-in fee (5% bank transfer, 7% card), with funds paid directly to your own bank account.</p>

                    <p>If you're a missions pastor or agency leader running compliance and payouts for a whole team, Pure Charity's infrastructure is purpose-built for that. If you're one missionary trying to raise your own monthly support, it likely isn't the tool you're looking for — and that's not a knock on it, it was never trying to be.</p>

                    <h2>What each platform actually costs</h2>

                    <p>This is where the comparison gets honest fast: Pure Charity doesn't publish a fee. Pricing is negotiated per organization, which is exactly why we left it out of the math in our <a href="/resources/true-cost-missionary-fundraising-platforms-2026">full seven-platform cost comparison</a> — there's no single number that's true for everyone. Devotion Fund charges <strong>5% all-in on bank transfer, 7% all-in on card</strong>, processing absorbed, no separate line items, no negotiation required.</p>

                    <p>If you're an individual trying to budget what a platform will actually cost you before you commit to it, "custom pricing, contact us" versus a public flat rate is itself a meaningful difference — regardless of which number turns out lower.</p>

                    <h2>Who Pure Charity is actually built for</h2>

                    <p>Pure Charity's real strength is running fundraising and compliance for a <em>group</em>: a sending organization with multiple missionaries, a short-term trip with dozens of participants, or a team-based campaign that needs centralized 501(c)(3) governance over how funds are received and released. That's genuinely hard infrastructure to build well, and if that's your situation, Pure Charity's tooling is purpose-built for it in a way a self-serve individual platform isn't trying to replicate.</p>

                    <p>What it isn't built for is a single missionary who wants to sign up, set a monthly goal, and start collecting recurring support without going through an organization's onboarding and approval process. There's no self-serve path for an individual the way there is on Devotion Fund.</p>

                    <h2>Where Devotion Fund is stronger for the individual</h2>

                    <p>Devotion Fund is built specifically for the missionary raising their own support, not a whole organization's: a recurring-by-default giving flow, direct payouts to your own bank account with no organizational approval layer, a built-in AI-assisted prayer-letter draft tool, and a donor CRM that shows who's engaged and who's drifting before they lapse. (See <a href="/resources/how-to-write-a-prayer-letter-people-actually-read">how to write a prayer letter people actually read</a> for the method, regardless of platform.) Setup takes about ten minutes, with no application or approval process to get started.</p>

                    <h2>Where Pure Charity is genuinely the better choice</h2>

                    <p>I'd point someone to Pure Charity, not Devotion Fund, if:</p>
                    <ul>
                        <li><strong>You're a missions pastor or agency leader</strong> running a centralized campaign across multiple missionaries or a short-term trip team, not raising support for yourself alone.</li>
                        <li><strong>You need organization-wide 501(c)(3) compliance</strong> — receipting, fund governance, and reporting across many participants at once.</li>
                        <li><strong>Custom, negotiated terms make sense for your scale</strong> — a large organization has the volume to make that conversation worthwhile.</li>
                    </ul>

                    <h2>Where Devotion Fund is the better choice</h2>

                    <p>Choose us when:</p>
                    <ul>
                        <li><strong>You're one missionary raising your own monthly support</strong>, not managing fundraising for an organization or team.</li>
                        <li><strong>You want to self-serve</strong> — sign up, publish a page, and start collecting support today, with no application or organizational approval process.</li>
                        <li><strong>You want a published, predictable fee</strong> you can budget around, not a custom quote.</li>
                        <li><strong>Keeping supporters engaged is your real bottleneck</strong> — the built-in updates and donor CRM exist specifically to solve that.</li>
                    </ul>

                    <p>If you're reading this as an agency leader building infrastructure for a whole team, this post isn't really an argument against Pure Charity — go talk to them. But if you're one missionary who just wants a page live today, <a href="/">start a page on Devotion Fund</a>; it takes about ten minutes, and I read every new signup personally.</p>

                    <h2>Frequently asked questions</h2>

                    <p><strong>Is Pure Charity a good fit for an individual missionary raising personal support?</strong><br />
                    Usually not on its own — it's built for sending organizations and team campaigns, not solo self-serve onboarding.</p>

                    <p><strong>How much does Pure Charity cost?</strong><br />
                    Pricing is custom and negotiated per organization; it isn't published, which is why we exclude it from our platform-by-platform fee comparison.</p>

                    <p><strong>Is Pure Charity or Devotion Fund cheaper?</strong><br />
                    There's no fair comparison to make — Pure Charity's rate is negotiated and unpublished, while Devotion Fund is a public 5% (bank transfer) or 7% (card) all-in.</p>

                    <p><strong>Who should choose Pure Charity over Devotion Fund?</strong><br />
                    Missions pastors and agency leaders running centralized, multi-team campaigns that need 501(c)(3) compliance infrastructure across many missionaries at once.</p>

                    <hr />
                    <p><small><em>Pure Charity's pricing model described here reflects its published positioning as custom, organization-level pricing, as cited in our full platform comparison. Devotion Fund's fee is all-in and public: 5% on bank transfers, 7% on card. We update this post if either organization changes its terms.</em></small></p>

                    <h2>About the author</h2>
                    <p>We're the team behind Devotion Fund. We built this platform for the individual missionary who wants to raise their own recurring support without going through an organization's onboarding process — while respecting that agencies running fundraising at team scale need different infrastructure than what we've built here.</p>
                    <p><small><em>Last updated: 2026-07-24.</em></small></p>
                </div>
                <div className="mt-8">
                    <EmailCapture page="/resources/pure-charity-vs-devotion-fund" />
                </div>
            </div>
            <Footer />
        </div>
    );
}
